import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/store/auth'
import { OperationCanceledException } from 'typescript'

interface User {
  _id?: string
  id?: string
  email: string
  first_name?: string
  last_name?: string
  first?: string
  last?: string
  organization?: string
  school?: string | null
  role: string
}

interface School {
  id: string
  name: string
}

interface Props {
  show: boolean
  user: User | null
  schools: School[]
  submitting?: boolean
  error?: string
  canEditRole?: boolean
  isSelfEdit?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditUserModal',
  props: {
    show: { type: Boolean },
    user: {},
    schools: {},
    submitting: { type: Boolean, default: false },
    error: { default: '' },
    canEditRole: { type: Boolean, default: true },
    isSelfEdit: { type: Boolean, default: false }
  },
  emits: ['close', 'submit'],
  setup(__props: any, { emit: __emit }) {

/* eslint-disable no-undef */
const authStore = useAuthStore()

const props = __props

const emit = __emit

const formRef = ref<any>(null)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

// Local editable copy of the user
const localUser = ref({
  email: '',
  first_name: '',
  last_name: '',
  organization: '',
  school: null as string | null,
  role: 'user',
  password: '',
  confirmPassword: ''
})

const showModal = computed({
  get: () => props.show,
  set: (value) => {
    if (!value) emit('close')
  }
})

const schoolOptions = computed(() => {
  return props.schools.map(school => ({
    title: school.name,
    value: school.id
  }))
})

// Get available roles from auth store with disabled state for roles above current user
const roleOptions = computed(() => {
  // Use the perceived role (authUser.role, which is overridden during "view as")
  const currentRole = authStore.authUser.role
  const currentUserLevel = authStore.roleHierarchy[currentRole] || 0

  // Get all roles from hierarchy
  const allRoles = Object.keys(authStore.roleHierarchy)
    .filter(role => role !== 'pending' && role !== 'root') // Exclude pending and root from options
    .map(role => {
      const roleLevel = authStore.roleHierarchy[role] || 0
      return {
        title: authStore.roleDisplayNames[role] || role,
        value: role,
        disabled: roleLevel > currentUserLevel // Disable roles above current user's level
      }
    })
    .sort((a, b) => {
      const levelA = authStore.roleHierarchy[a.value] || 0
      const levelB = authStore.roleHierarchy[b.value] || 0
      return levelB - levelA // Sort by level descending
    })

    for (const roleOption of allRoles) {
      if(roleOption.disabled){
        //remove disabled roles from the list
        const index = allRoles.indexOf(roleOption)
        if (index > -1) {
          allRoles.splice(index, 1)
        }
      }
    }

  return allRoles
})

const getRoleHint = () => {
  if (props.isSelfEdit) {
    return 'You cannot change your own role to prevent lockout'
  }
  if (!props.canEditRole) {
    return 'Only admins can change user roles'
  }
  return ''
}

const closeModal = () => {
  // Reset local copy
  localUser.value = {
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    school: null,
    role: 'user',
    password: '',
    confirmPassword: ''
  }
  showPassword.value = false
  showConfirmPassword.value = false
  emit('close')
}

const submitUser = async () => {
  const { valid } = await formRef.value.validate()

  if (!valid) {
    return
  }
  const userData: any = {
    email: localUser.value.email,
    first_name: localUser.value.first_name,
    last_name: localUser.value.last_name,
    organization: localUser.value.organization,
    school: localUser.value.school,
    role: localUser.value.role
  }

  // Only include password if it's being changed
  if (localUser.value.password) {
    userData.password = localUser.value.password
  }

  emit('submit', userData)
}

// Watch for user prop changes and populate local copy
watch(() => props.user, (newUser) => {
  if (newUser) {
    console.log('Loading user into edit modal:', newUser)
    // Create a fresh copy of the user data
    localUser.value = {
      email: newUser.email || '',
      first_name: newUser.first_name || newUser.first || '',
      last_name: newUser.last_name || newUser.last || '',
      organization: newUser.organization || '',
      school: newUser.school || null,
      role: newUser.role || 'user',
      password: '',
      confirmPassword: ''
    }
    console.log('Local user copy created:', localUser.value)
  }
}, { immediate: true, deep: true })

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showModal).value = $event)),
    "max-width": "600px",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, null, {
            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
              _createElementVNode("span", { class: "text-h5" }, "Edit User", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_form, {
                ref_key: "formRef",
                ref: formRef
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: localUser.value.email,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localUser.value.email) = $event)),
                    label: "Email",
                    type: "email",
                    variant: "outlined",
                    rules: [
              v => !!v || 'Email is required',
              v => /.+@.+\..+/.test(v) || 'Email must be valid'
            ],
                    required: "",
                    class: "mb-2"
                  }, null, 8, ["modelValue", "rules"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: localUser.value.first_name,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localUser.value.first_name) = $event)),
                    label: "First Name",
                    variant: "outlined",
                    class: "mb-2"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: localUser.value.last_name,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localUser.value.last_name) = $event)),
                    label: "Last Name",
                    variant: "outlined",
                    class: "mb-2"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: localUser.value.organization,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localUser.value.organization) = $event)),
                    label: "Organization",
                    variant: "outlined",
                    class: "mb-2"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_select, {
                    modelValue: localUser.value.school,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((localUser.value.school) = $event)),
                    items: schoolOptions.value,
                    label: "School",
                    variant: "outlined",
                    clearable: "",
                    class: "mb-2"
                  }, null, 8, ["modelValue", "items"]),
                  _createVNode(_component_v_select, {
                    modelValue: localUser.value.role,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localUser.value.role) = $event)),
                    items: roleOptions.value,
                    label: "Role",
                    variant: "outlined",
                    rules: [v => !!v || 'Role is required'],
                    required: "",
                    disabled: !__props.canEditRole || __props.isSelfEdit,
                    hint: getRoleHint(),
                    "persistent-hint": "",
                    class: "mb-2"
                  }, null, 8, ["modelValue", "items", "rules", "disabled", "hint"]),
                  _createVNode(_component_v_divider, { class: "my-4" }),
                  _cache[12] || (_cache[12] = _createElementVNode("div", { class: "text-subtitle-1 mb-3" }, "Password Reset (Optional)", -1)),
                  _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-caption text-grey mb-3" }, "Leave blank to keep current password", -1)),
                  _createVNode(_component_v_text_field, {
                    modelValue: localUser.value.password,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((localUser.value.password) = $event)),
                    label: "New Password",
                    type: showPassword.value ? 'text' : 'password',
                    variant: "outlined",
                    rules: [
              v => !v || v.length >= 8 || 'Password must be at least 8 characters'
            ],
                    "append-inner-icon": showPassword.value ? 'mdi-eye-off' : 'mdi-eye',
                    "onClick:appendInner": _cache[7] || (_cache[7] = ($event: any) => (showPassword.value = !showPassword.value)),
                    class: "mb-2"
                  }, null, 8, ["modelValue", "type", "rules", "append-inner-icon"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: localUser.value.confirmPassword,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((localUser.value.confirmPassword) = $event)),
                    label: "Confirm New Password",
                    type: showConfirmPassword.value ? 'text' : 'password',
                    variant: "outlined",
                    rules: [
              v => !localUser.value.password || !!v || 'Please confirm password',
              v => !localUser.value.password || v === localUser.value.password || 'Passwords must match'
            ],
                    "append-inner-icon": showConfirmPassword.value ? 'mdi-eye-off' : 'mdi-eye',
                    "onClick:appendInner": _cache[9] || (_cache[9] = ($event: any) => (showConfirmPassword.value = !showConfirmPassword.value))
                  }, null, 8, ["modelValue", "type", "rules", "append-inner-icon"]),
                  (__props.error)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 0,
                        type: "error",
                        variant: "tonal",
                        class: "mt-4"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(__props.error), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 512)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                color: "grey",
                onClick: closeModal,
                disabled: __props.submitting
              }, {
                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                  _createTextVNode(" Cancel ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_component_v_btn, {
                color: "primary",
                onClick: submitUser,
                loading: __props.submitting
              }, {
                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                  _createTextVNode(" Update User ", -1)
                ]))]),
                _: 1
              }, 8, ["loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})